import { useEffect, useMemo, useState, useCallback } from "react";
import { GridColDef } from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";

import HeightWeightCreate from "../create";
import { calculateBMI } from "../create/helpers/bmiCalculator";
import { useTranslate } from "src/locales";
import BaseMedCardList from "src/components/shared-components/medcard/BaseMedCardList";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { useAppSelector } from "src/app/hooks";
import { referenceService } from "src/api/references/referenceService";
import { GetPatientHeightWeightTraceByIdResponseDto, HeightWeightTraceItemDto } from "src/api/references/referenceServicesDtos";
import ByzGrdDeleteButton from "src/components/core-components/mui/buttons-grid/ByzGrdDeleteButton";
import ByzGrdEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { usePatientUpn } from "../../hooks/usePatientUpn";

type HeightWeightRow = {
  id: number | string;
  date?: string;
  height?: number | null;
  weight?: number | null;
  vki?: number | null;
  description?: string | null;
  write_on_epicrise?: string | null;
  vki_assesment?: string | null;
  cuser?: string | null;
};

function HeightWeightList() {
  const { t } = useTranslate();
  const { id: routePatientId } = useParams();
  const mainTabs = useAppSelector((state) => state.tabs.mainTabs);
  const activeTabIndex = useAppSelector((state) => state.tabs.activeTabIndex);
  const selectedPatientId = mainTabs?.[activeTabIndex]?.data?.patientId;

  const [rows, setRows] = useState<HeightWeightRow[]>([]);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editInitialData, setEditInitialData] = useState<{
    id: number;
    height?: number | string | null;
    weight?: number | string | null;
    vki?: number | string | null;
    description?: string | null;
    write_on_epicrise?: string | null;
    vki_mcpd_id?: number | null;
    ptime?: string | null;
  } | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    type: "success" | "error";
  }>({ open: false, message: "", type: "success" });

  const patientIdNumber = useMemo(
    () => Number(routePatientId || selectedPatientId || 0),
    [routePatientId, selectedPatientId]
  );

  const initialUpn = mainTabs?.[activeTabIndex]?.data?.upn ?? null;
  const { upn, setUpn, isFetchingUpn } = usePatientUpn(patientIdNumber, initialUpn);

  const loadData = useCallback(async () => {
    if ((!patientIdNumber || patientIdNumber <= 0) && !upn) return;
    if (!upn && isFetchingUpn) return;

    try {
      const response = await referenceService.patient.GetPatientHeightWeightTrace({
        pt_id: patientIdNumber > 0 ? patientIdNumber : undefined,
        upn: upn ?? undefined,
        search_date: dayjs().toISOString(),
        all_recs: 1, // tüm kayıtları getir
      });

      const rawData = Array.isArray(response) ? response : (response as { data?: unknown })?.data ?? response;
      const parsedData =
        typeof rawData === "string" ? JSON.parse(rawData) : rawData;

      if (Array.isArray(parsedData)) {
        const formattedRows = parsedData
          .filter((item): item is HeightWeightTraceItemDto => !!item)
          .map((item: HeightWeightTraceItemDto, index: number) => {
            const dateStr =
              item.date ??
              item.ptime ??
              item.ctime ??
              item.PTIME ??
              item.CTIME ??
              null;
            const formattedDate = dateStr
              ? dayjs(dateStr).format("DD.MM.YYYY HH:mm")
              : "-";

            const heightVal = item.height ?? item.HEIGHT ?? "";
            const weightVal = item.weight ?? item.WEIGHT ?? "";
            const vkiVal = item.vki ?? item.VKI ?? null;

            const bmiCalc = calculateBMI(
              weightVal !== null && weightVal !== undefined
                ? String(weightVal)
                : "",
              heightVal !== null && heightVal !== undefined
                ? String(heightVal)
                : ""
            );

            const vkiFinal =
              vkiVal !== null && vkiVal !== undefined && String(vkiVal) !== ""
                ? Number(vkiVal)
                : bmiCalc.vki
                ? Number(bmiCalc.vki)
                : null;

            const obesityFinal =
              item.vki_assesment ??
              item.VKI_ASSESMENT ??
              (bmiCalc.obesityStatus ? bmiCalc.obesityStatus : null);

            return {
              id: item.id ?? item.ID ?? index,
              date: formattedDate,
              height: typeof heightVal === "number" ? heightVal : (heightVal ? Number(heightVal) : null),
              weight: typeof weightVal === "number" ? weightVal : (weightVal ? Number(weightVal) : null),
              vki: vkiFinal,
              description: item.description ?? item.DESCRIPTION ?? null,
              write_on_epicrise:
                item.write_on_epicrise ?? item.WRITE_ON_EPICRISE ?? null,
              vki_assesment: obesityFinal,
              cuser: item.cuser ?? item.CUSER ?? null,
            };
          });
        setRows(formattedRows);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error("GetPatientHeightWeightTrace error:", error);
      setRows([]);
    }
  }, [patientIdNumber, upn, isFetchingUpn]);

  const handleDeleteClick = useCallback((id?: number | string | null) => {
    if (!id && id !== 0) return;
    const numericId = Number(id);
    if (Number.isNaN(numericId)) return;
    setDeleteItemId(numericId);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeleteItemId(null);
  }, []);

  const handleDeleteConfirm = useCallback(async (): Promise<boolean> => {
    if (!deleteItemId && deleteItemId !== 0) return false;
    try {
      setIsDeleting(true);
      setDeletingId(deleteItemId);
      await referenceService.patient.DeletePatientHeightWeightTrace(deleteItemId);
      await loadData();
      setDeleteDialogOpen(false);
      setDeleteItemId(null);
      return true;
    } catch (error) {
      console.error("DeletePatientHeightWeightTrace error:", error);
      return false;
    } finally {
      setDeletingId(null);
      setIsDeleting(false);
    }
  }, [deleteItemId, loadData]);

  const openEdit = useCallback(async (id?: number | string | null) => {
    if (!id && id !== 0) return;
    const numericId = Number(id);
    if (Number.isNaN(numericId)) return;
    try {
      setEditLoading(true);
      setEditId(numericId);
      const res: GetPatientHeightWeightTraceByIdResponseDto = await referenceService.patient.GetPatientHeightWeightTraceById(
        numericId
      );
      const rawData = Array.isArray(res) ? res : (res as { data?: unknown })?.data ?? res;
      const parsed: HeightWeightTraceItemDto | HeightWeightTraceItemDto[] =
        typeof rawData === "string" ? JSON.parse(rawData) : rawData;
      const item: HeightWeightTraceItemDto = Array.isArray(parsed) ? parsed[0] : parsed;
      setEditInitialData({
        id: numericId,
        height: item?.height ?? item?.HEIGHT ?? "",
        weight: item?.weight ?? item?.WEIGHT ?? "",
        vki: item?.vki ?? item?.VKI ?? "",
        description: item?.description ?? item?.DESCRIPTION ?? "",
        write_on_epicrise:
          item?.write_on_epicrise ?? item?.WRITE_ON_EPICRISE ?? "0",
        vki_mcpd_id: item?.vki_mcpd_id ?? item?.VKI_MCPD_ID ?? 0,
        ptime: item?.ptime ?? item?.PTIME ?? dayjs().toISOString(),
      });
      setEditOpen(true);
    } catch (error) {
      console.error("GetPatientHeightWeightTraceById error:", error);
    } finally {
      setEditLoading(false);
    }
  }, []);

  const handleShowMessage = useCallback(
    ({ message, type }: { message: string; type: "success" | "error" }) => {
      setSnackbar({ open: true, message, type });
    },
    []
  );

  const columns: GridColDef<HeightWeightRow>[] = useMemo(
    () => [
      {
        field: "edit",
        headerName: "",
        width: 70,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
            <ByzGrdEditButton
              disabled={editLoading && editId === Number(params?.row?.id)}
              onClick={() => openEdit(params?.row?.id)}
            />
          </Box>
        ),
      },
      {
        field: "date",
        headerName: t("height-weight.date"),
        flex: 1,
        minWidth: 260, // genişlik artırıldı
        valueGetter: (paramsOrValue: any, rowOrUndefined?: HeightWeightRow) => {
          try {
            const row = paramsOrValue?.row ?? rowOrUndefined;
            if (!row) return "-";
            return row.date ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        field: "height",
        headerName: t("height-weight.height-cm"),
        flex: 1,
        valueGetter: (paramsOrValue: any, rowOrUndefined?: HeightWeightRow) => {
          try {
            const row = paramsOrValue?.row ?? rowOrUndefined;
            if (!row) return "-";
            return row.height ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        field: "weight",
        headerName: t("height-weight.weight-kg"),
        flex: 1,
        valueGetter: (paramsOrValue: any, rowOrUndefined?: HeightWeightRow) => {
          try {
            const row = paramsOrValue?.row ?? rowOrUndefined;
            if (!row) return "-";
            return row.weight ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        field: "vki",
        headerName: t("height-weight.bmi"),
        flex: 1,
        valueGetter: (paramsOrValue: any, rowOrUndefined?: HeightWeightRow) => {
          try {
            const row = paramsOrValue?.row ?? rowOrUndefined;
            if (!row) return "-";
            return row.vki ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        field: "vki_assesment",
        headerName: t("height-weight.bmi-obesity-assessment"),
        flex: 1.2,
        valueGetter: (paramsOrValue: any, rowOrUndefined?: HeightWeightRow) => {
          try {
            const row = paramsOrValue?.row ?? rowOrUndefined;
            if (!row) return "-";
            return row.vki_assesment ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        field: "cuser",
        headerName: t("height-weight.user"),
        flex: 1,
        valueGetter: (paramsOrValue: any, rowOrUndefined?: HeightWeightRow) => {
          try {
            const row = paramsOrValue?.row ?? rowOrUndefined;
            if (!row) return "-";
            return row.cuser ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        field: "description",
        headerName: t("height-weight.description"),
        flex: 1.5,
        valueGetter: (paramsOrValue: any, rowOrUndefined?: HeightWeightRow) => {
          try {
            const row = paramsOrValue?.row ?? rowOrUndefined;
            if (!row) return "-";
            return row.description ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        field: "write_on_epicrise",
        headerName:
          t("height-weight.epicrisis-output") ||
          t("height-weight.write-on-epicrise") ||
          "Epikriz",
        width: 140,
        valueGetter: (paramsOrValue: any, rowOrUndefined?: HeightWeightRow) => {
          try {
            const row = paramsOrValue?.row ?? rowOrUndefined;
            if (!row) return "-";
            const val = row.write_on_epicrise ?? "-";
            const yesLabel = t("yes") || "Evet";
            const noLabel = t("no") || "Hayır";
            if (val === "1" || val === 1) return yesLabel;
            if (val === "2" || val === 2) return noLabel;
            return "-";
          } catch {
            return "-";
          }
        },
      },
      {
        field: "delete",
        headerName: "",
        width: 70,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
            <ByzGrdDeleteButton
              disabled={deletingId === Number(params?.row?.id)}
              onClick={() => handleDeleteClick(params?.row?.id)}
            />
          </Box>
        ),
      },
    ],
    [t, deletingId, handleDeleteClick, editId, editLoading, openEdit]
  );


  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <>
      <Backdrop
        open={isDeleting}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <ByzDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        patientId={deleteItemId?.toString() || ""}
        title={t("card.delete-confirmation") || "Silme Onayı"}
        content={t("card.delete-confirmation-message") || "Bu kaydı silmek istediğinizden emin misiniz?"}
        onConfirm={handleDeleteConfirm}
        successMessage={t("delete-success") || "Kayıt başarıyla silindi"}
        errorMessage={t("delete-error") || "Kayıt silinirken hata oluştu"}
        onSuccess={(success) => {
          if (success) {
            setDeleteDialogOpen(false);
            setDeleteItemId(null);
          }
        }}
      />

      <BaseMedCardList
        title={t("height-weight.title")}
        columns={columns}
        rows={rows}
        CreateComponent={HeightWeightCreate}
        createComponentProps={{ onSaved: loadData, onShowMessage: handleShowMessage }}
        pageSize={10}
      />

      <OpenComponentDialog
        title={t("height-weight.update")}
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditId(null);
        }}
        isMedCard
      >
        <HeightWeightCreate
          mode="edit"
          recordId={editId ?? undefined}
          initialData={editInitialData || undefined}
          onSaved={async () => {
            await loadData();
          }}
          onShowMessage={handleShowMessage}
          onCloseModal={() => {
            setEditOpen(false);
            setEditId(null);
          }}
        />
      </OpenComponentDialog>

      <ByzMuiSnackBar
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        title=""
        message={snackbar.message}
        type={snackbar.type}
        anchorOrigin={snackbar.type === "error" ? { vertical: "top", horizontal: "right" } : { vertical: "bottom", horizontal: "right" }}
      />
    </>
  );
}

export default HeightWeightList;
